import { FC, useEffect, useState, ReactNode } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender
} from '@tanstack/react-table';

interface TableProps {
  classNameMain?: string;
  tableData?: any[];
  classNameBody?: string;
  tableColumns?: any[];
  classNameHead?: string;
  className?: string;
  initialState?: any;
  classNameTd?: string;
  children?: ReactNode;
  classNameTBody?: string;
  onRowClick?: (row: any) => void;
}

const Table: FC<TableProps> = ({
  classNameMain,
  tableData,
  classNameBody,
  tableColumns,
  classNameHead,
  className,
  initialState,
  classNameTd,
  children,
  classNameTBody,
  onRowClick
}) => {
  const [data, setData] = useState<any[]>(tableData ?? []);
  const [columns, setColumns] = useState<any[]>(tableColumns ?? []);

  useEffect(() => {
    setData(tableData ?? []);
    setColumns(tableColumns ?? []);
  }, [tableData, tableColumns]);

  const tableInstance = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState
  });

  return (
    <div
      className={`bg-white rounded-lg border border-gray-200 ${classNameMain || ''}`}>
      <table className={`w-full ${className || ''}`}>
        <thead className="bg-gray-50 border-b border-gray-200">
          {tableInstance.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <th
                  key={header.id}
                  className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${classNameHead || ''}`}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody
          className={`bg-white divide-y divide-gray-200 ${classNameTBody || ''}`}>
          {tableInstance.getRowModel().rows.map(row => {
            return (
              <tr
                key={row.id}
                onClick={() => onRowClick?.(row.original)}
                className={`hover:bg-gray-50 transition-colors ${onRowClick ? 'cursor-pointer' : ''} ${classNameBody || ''}`}>
                {row.getVisibleCells().map(cell => (
                  <td
                    key={cell.id}
                    className={`px-4 py-2 whitespace-nowrap text-sm text-gray-900 ${classNameTd || ''}`}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            );
          })}
          {children}
        </tbody>
      </table>
    </div>
  );
};
export { Table };
